<template>
  <div class="category-scope-picker">
    <el-cascader
      ref="cascader"
      v-model="selectedIds"
      :options="options"
      :props="cascaderProps"
      :show-all-levels="false"
      :loading="loading"
      clearable
      collapse-tags
      filterable
      placeholder="请选择一级、二级或三级分类（可多选）"
      popper-class="coupon-category-cascader"
      class="category-cascader"
      @change="handleChange"
    />

    <div class="selection-meta">
      <span>已选 {{ selectedIds.length }} 个末级分类</span>
      <span class="selection-help">勾选一级或二级分类，将自动选中其下全部分类</span>
    </div>

    <div v-if="summaryTags.length" class="selection-summary">
      <el-tag
        v-for="tag in visibleSummaryTags"
        :key="tag.key"
        size="small"
        effect="plain"
        :title="tag.path"
      >
        {{ tag.label }}
      </el-tag>
      <el-tag v-if="hiddenSummaryCount" size="small" type="info" effect="plain">
        另有 {{ hiddenSummaryCount }} 项
      </el-tag>
    </div>
  </div>
</template>

<script>
import { getCategoryTree } from "@/api/category";

export default {
  name: "CategoryScopePicker",
  props: {
    value: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      options: [],
      selectedIds: [],
      nodeMap: {},
      cascaderProps: {
        multiple: true,
        checkStrictly: false,
        emitPath: false,
        value: "value",
        label: "label",
        children: "children"
      }
    };
  },
  computed: {
    summaryTags() {
      const selected = new Set(this.selectedIds.map(String));
      const tags = [];
      this.options.forEach(root => {
        this.collectSummary(root, selected, tags, []);
      });
      return tags;
    },
    visibleSummaryTags() {
      return this.summaryTags.slice(0, 8);
    },
    hiddenSummaryCount() {
      return Math.max(0, this.summaryTags.length - this.visibleSummaryTags.length);
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        this.applyValue(value);
      }
    }
  },
  created() {
    this.loadCategories();
  },
  methods: {
    async loadCategories() {
      this.loading = true;
      try {
        const response = await getCategoryTree({});
        const rows = response.bizcode === 100 ? response.data || [] : [];
        this.nodeMap = {};
        this.options = rows.map(row => this.normalizeNode(row, []));
        this.applyValue(this.value);
      } finally {
        this.loading = false;
      }
    },
    normalizeNode(row, parentPath) {
      const value = String(row.id);
      const path = parentPath.concat(row.name);
      const children = (row.children || []).map(child => this.normalizeNode(child, path));
      const node = {
        value,
        label: row.name,
        path,
        children: children.length ? children : undefined
      };
      this.nodeMap[value] = node;
      return node;
    },
    applyValue(value) {
      const values = (value || []).map(String).filter(Boolean);
      if (!this.options.length) {
        this.selectedIds = values;
        return;
      }
      const leafIds = [];
      values.forEach(id => {
        const node = this.nodeMap[id];
        if (node) {
          this.collectLeafIds(node, leafIds);
        }
      });
      this.selectedIds = Array.from(new Set(leafIds));
    },
    collectLeafIds(node, result) {
      if (!node.children || !node.children.length) {
        result.push(String(node.value));
        return;
      }
      node.children.forEach(child => this.collectLeafIds(child, result));
    },
    handleChange(value) {
      const normalized = (value || []).map(String);
      this.selectedIds = normalized;
      this.$emit("input", normalized);
      this.$emit("change", normalized);
    },
    collectSummary(node, selected, result, parentPath) {
      const leafIds = [];
      this.collectLeafIds(node, leafIds);
      const selectedLeafIds = leafIds.filter(id => selected.has(id));
      if (!selectedLeafIds.length) return;

      const currentPath = parentPath.concat(node.label);
      if (selectedLeafIds.length === leafIds.length && node.children && node.children.length) {
        result.push({
          key: String(node.value),
          label: `${node.label}（全部）`,
          path: currentPath.join(" / ")
        });
        return;
      }

      if (node.children && node.children.length) {
        node.children.forEach(child => this.collectSummary(child, selected, result, currentPath));
        return;
      }

      result.push({
        key: String(node.value),
        label: currentPath.join(" / "),
        path: currentPath.join(" / ")
      });
    }
  }
};
</script>

<style scoped lang="scss">
.category-scope-picker {
  width: 100%;
}

.category-cascader {
  width: 100%;
}

.selection-meta {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 8px;
  color: #606266;
  font-size: 12px;
  line-height: 20px;
}

.selection-help {
  color: #909399;
  text-align: right;
}

.selection-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  padding: 10px 12px;
  background: #f7f8fb;
  border-radius: 6px;
}

@media (max-width: 768px) {
  .selection-meta {
    display: block;
  }

  .selection-help {
    display: block;
    text-align: left;
  }
}
</style>

<style lang="scss">
.coupon-category-cascader {
  .el-cascader-menu {
    min-width: 190px;
    max-width: 260px;
  }

  .el-cascader-node__label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
